import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { erApiFeil } from '../../api/feil.ts';
import { useLeggTilPerson, type RoomRole, type Romvalg } from '../../api/useAdminUsers.ts';
import { useMe } from '../../api/useMe.ts';
import { useAuth } from '../../auth/AuthProvider.tsx';
import { Knapp } from '../../innboks/Detalj.tsx';
import type { Rom } from '../../skall/rom.ts';
import { useTilgang } from '../../skall/tilgang.ts';
import { Ikon } from '../../status/Ikon.tsx';
import { BareEier } from '../BareEier.tsx';
import { PersonRad, Setning, Tilgangsvalg, Topp } from './felles.tsx';
import type { ListeTilstand } from './ListeSide.tsx';
import { setningFor } from './modell.ts';

const GYLDIG = /^[^\s@]+@[^\s@]+$/;

/**
 * «Legg til person» (`/organisasjon/brukere/ny`, M4.8 steg 3) — samme rader som personen, fra
 * start. Ordet er eierens; mekanikken er sete-først og uendret (DD-68): kontoen kommer ved
 * første innlogging, og panelet sender ingen e-post.
 *
 * Validering etter interaksjon: `aria-invalid` først etter blur eller lagreforsøk; til da bærer
 * knappens grunn kravet. 409 `admin_user_email_taken` sier aldri HVOR adressen finnes (K2). Ved
 * feil står teksten.
 */
export function NySide() {
  const tilgang = useTilgang();
  if (!tilgang.kjent) return null;
  if (!tilgang.eier) return <BareEier />;
  return <Skjema />;
}

function Skjema() {
  const me = useMe();
  const auth = useAuth();
  const naviger = useNavigate();
  const leggTil = useLeggTilPerson();
  const feilId = useId();
  const rom = me.data?.rooms;
  const rommene: readonly Rom[] = useMemo(() => rom ?? [], [rom]);

  const [epost, setEpost] = useState('');
  const [eier, setEier] = useState(false);
  // Standard er Behandler i Innspill — den vanligste grunnen til å legge til noen.
  const [valg, setValg] = useState<Romvalg>({ innspill: 'behandler' });
  const [provd, setProvd] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);

  // Eksempelet og plassholderen bruker domenet til eieren som er logget inn.
  const egen = auth.tilstand.navn === 'innlogget' ? auth.tilstand.epost : undefined;
  const domene = egen?.split('@')[1] ?? 'leietaker.no';

  const adresse = epost.trim().toLowerCase();
  const ugyldig = adresse !== '' && !GYLDIG.test(adresse);
  const ingenRom = !eier && !rommene.some((r) => valg[r]);
  const feiltekst = feil ?? (provd && ugyldig ? `Skriv en jobbadresse, f.eks. navn@${domene}.` : null);
  const sperret = adresse === '' ? 'Skriv en jobbadresse' : ingenRom ? 'Velg minst ett rom' : leggTil.isPending ? 'Legger til …' : null;

  const send = useCallback(() => {
    if (sperret) return;
    setProvd(true);
    setFeil(null);
    if (ugyldig) return;
    leggTil
      .mutateAsync({ email: adresse, eier, rom: eier ? {} : Object.fromEntries(rommene.map((r) => [r, valg[r] ?? null])) })
      .then((ny) => {
        const tilstand: ListeTilstand = { kvittering: `Personen er lagt til. Be ${ny.email} logge inn på panelet med adressen — kontoen opprettes ved første innlogging. Panelet sender ingen e-post.` };
        void naviger('/organisasjon/brukere', { state: tilstand });
      })
      .catch((e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.'));
  }, [sperret, ugyldig, leggTil, adresse, eier, rommene, valg, naviger]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        send();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [send]);

  return (
    <>
      <div className="panel-pb">
        <Topp tilbake={{ til: 'Panelbrukere', href: '/organisasjon/brukere' }} tittel="Legg til person" skjultTittel />
        <div className="panel-pb-innhold panel-pb-person">
          <div className="panel-pb-rader-person">
            <PersonRad navn="Jobbadresse">
              <input
                type="email"
                className="panel-pb-felt"
                value={epost}
                onChange={(e) => {
                  setEpost(e.target.value);
                  setFeil(null);
                }}
                onBlur={() => setProvd(adresse !== '')}
                placeholder={`navn@${domene}`}
                maxLength={254}
                autoComplete="off"
                aria-label="Jobbadresse"
                aria-invalid={feiltekst ? true : undefined}
                aria-describedby={feiltekst ? feilId : undefined}
                data-testid="pb-ny-epost"
              />
              {feiltekst ? (
                <span id={feilId} role="alert" className="panel-pb-felt-feil" data-testid="pb-ny-feil">
                  <Ikon navn="warning-outline" />
                  {feiltekst}
                </span>
              ) : null}
            </PersonRad>
            <Tilgangsvalg eier={eier} valg={valg} rommene={rommene} onRolle={setEier} onRom={(r: Rom, rolle: RoomRole | null) => setValg((x) => ({ ...x, [r]: rolle }))} ny />
          </div>
          <Setning s={setningFor(adresse || 'personen', eier, valg, rommene, true)} />
          {/* Den ENE hjelpesetningen på siden (DD-68, mekanikken uendret). */}
          <p className="panel-pb-hjelp">Kontoen opprettes første gang personen logger inn på panelet med nøyaktig denne adressen — panelet sender ingen e-post.</p>
        </div>
      </div>
      <footer className="panel-innst-fot panel-pb-fot" data-testid="pb-fot">
        <Knapp label="Legg til person" kbd="Ctrl+S" primaer onClick={send} grunn={sperret} testid="pb-ny-lagre" />
        <span className="panel-innst-fot-hva">Står i lista som «Venter på første innlogging»</span>
      </footer>
    </>
  );
}
